import isArray from 'lodash/isArray'
import uniqBy from 'lodash/uniqBy'

import { DataReport } from 'Core/common/DataReport/types'
import { PreviewSections } from 'Core/interfaces'

const LIMIT_OF_VALDIATION_GROUPS = 2
const LIMIT_OF_EITHER_GROUPS = 2

// Rules for Report type Data validation
// In case "isRequired" is true, validation is equal to default "required" validation, ignore other validation rules
// sqlParameters is always mandatory
// No validation groups logic:
// 1. Should be correct either/or count, but cannot be more then 1 either/or (without validation group)
// Validation groups logic:
// 1. Cannot be less then 2 unique validation groups
// 2. Cannot be more then 1 either group
// 3. Should be correct either/or inside validation groups (each group should have only all 'either' or all 'or' values)
// Correct validaiton logic:
// 1. isRequired - default validation
// 2. Either/or - should be selected one of fields (the one with 'either' flag or the one with 'or' flag) - same for groups
// 3. For Mandatory fields - use "prefix" field. So in component you can see, that you need to combine reportWhereParameters and sqlParameters validation fields.
export class ReportDataValidation {
	constructor(public data: DataReport.Basic, public parameters: DataReport.Form) {}

	get mandatoryFields() {
		return this.parameters.reportWhereParameters.filter(
			(par) => par.isRequired && (!par.value || !!(par.value as LabelValue[]).length)
		)
	}

	get filteredGroups() {
		return this.parameters.reportWhereParameters.filter((group) => !!group.validationGroup)
	}

	get isCheckbox() {
		return this.data.fieldName === PreviewSections.Checkbox
	}

	private requiredValidation() {
		if (this.data.isRequired) {
			return {
				isValid: isArray(this.data.value) ? !!this.data.value.length : !!this.data.value,
				additionalData: { prefix: 'Mandatory' },
				errMsg: `${this.mandatoryFields.map((field) => field.label).join(', ')}`
			}
		}
	}

	private groupValidation() {
		const uniqValidationGroups = uniqBy(this.filteredGroups, 'validationGroup')
		if (!uniqValidationGroups.length) {
			return
		}

		if (!this.data.either && !this.data.or && !this.data.isRequired) {
			return { isValid: true }
		}

		if (uniqValidationGroups.length < LIMIT_OF_VALDIATION_GROUPS) {
			return { isValid: false, errMsg: 'Invalid Validation Group structure' }
		}

		// check if valid groups
		if (!this.filteredGroups.every((group) => group.either || group.or)) {
			return { isValid: false, errMsg: 'Invalid structure' }
		}

		const validationGroupValues = uniqValidationGroups.reduce((acc, data) => {
			if (data.validationGroup) {
				acc.push(data.validationGroup)
			}
			return acc
		}, [] as string[])
		const eitherGroup = this.filteredGroups.filter((group) => group.either)

		// cannot be more then 1 either group
		if (eitherGroup.length > LIMIT_OF_EITHER_GROUPS) {
			return {
				isValid: false,
				errMsg: 'Cannot be more then 1 either group, Invalid structure'
			}
		}

		const isCorrectEitherOr = validationGroupValues.every((group) => {
			const filtered = this.filteredGroups.filter((value) => value.validationGroup === group)
			return (
				filtered.every((value) => value.either && !value.or) ||
				filtered.every((value) => value.or && !value.either)
			)
		})

		if (!isCorrectEitherOr) {
			return { isValid: false, errMsg: 'Invalid either/or structure' }
		}

		const formattedGroups = this.filteredGroups.reduce((acc, group) => {
			if (group.validationGroup) {
				acc[group.validationGroup] = acc[group.validationGroup]
					? [...acc[group.validationGroup], group]
					: [group]
			}
			return acc
		}, {} as { [key: string]: DataReport.Basic[] })

		let isValid = false

		Object.keys(formattedGroups).forEach((value) => {
			isValid = !isValid ? formattedGroups[value].every((item) => !!item.value) : isValid
		})

		const filteredFormattedGroups = Object.values(formattedGroups).filter((group) => {
			return !group.find((value) => value.either)
		})

		const eitherText = eitherGroup.map((field) => field.label).join(' and ')

		const orText = filteredFormattedGroups
			.map((el) =>
				el
					.map((value, index) => (!index ? `(${value.label}` : `${value.label})`))
					.join(' and ')
			)
			.join(' or ')

		return {
			isValid,
			errMsg: `Please specify the parameters for either (${eitherText}) or ${orText}`
		}
	}

	private standartEitherOrValidation() {
		const { reportWhereParameters } = this.parameters
		let either = 0
		let or = 0
		reportWhereParameters.forEach((par) => {
			par.either && either++
			par.or && or++
		})

		// no validation
		if (!either && !or) {
			return { isValid: true }
		}

		if ((either > 1 && or > 1) || either !== or) {
			return { isValid: false, errMsg: 'Invalid either/or count' }
		}

		const eitherField = reportWhereParameters.find((par) => par.either)
		const orField = reportWhereParameters.find((par) => par.or)
		const isValid = !!eitherField?.value || !!orField?.value

		if (!this.data.either && !this.data.or) {
			return { isValid: true }
		}
		const eitherText = eitherField?.label
		const orText = orField?.label
		return {
			isValid: isValid,
			errMsg: `Please specify the parameters for either ${eitherText} or ${orText}`
		}
	}

	validate() {
		//no validation for checkbox
		if (this.isCheckbox) {
			return { isValid: true }
		}

		const requiredValidation = this.requiredValidation()
		if (requiredValidation) {
			return requiredValidation
		}

		const groupValidation = this.groupValidation()
		if (groupValidation) {
			return groupValidation
		}

		return this.standartEitherOrValidation()
	}
}
